'use client';

/**
 * The picture the homepage's journey tells its story with: one project,
 * building itself as the visitor scrolls through the five steps.
 *
 * It is drawn in the Plan view's own language (studio/plan/PlanView.tsx) —
 * ink walls, a speaker box with its letter, the seat in the seat colour, the
 * listening triangle in the acoustic blue — so the homepage shows the product
 * rather than an illustration of one. Everything is cumulative: each step
 * adds to what the last one drew, and a step's arrival is a transition of the
 * drawing already on screen, never a swap of pictures.
 *
 *   0 gear       three real parts and their total; everything connects
 *   1 room       the walls draw themselves, the dimensions arrive
 *   2 place      speakers, sub and seat drop in; the triangle measures 60°
 *   3 optimize   the system glides to better positions, panels go up, the
 *                field in the room evens out
 *   4 result     the response at the seat, before and after
 *
 * A strip of the Studio's four lenses rides on top, its raised thumb moving to
 * the lens each step happens in. The whole picture is hidden from assistive
 * tech: the step list beside it is the content, and nothing here says
 * anything the list does not.
 *
 * Motion: positions on the house spring, fades on the short fade, the walls
 * and curves drawn by stroke-dashoffset. Reduced motion shows each state at
 * rest (`.journey-stage` in globals.css drops every transition inside it).
 */

import type { CSSProperties, ReactNode } from 'react';
import Image from 'next/image';
import { STUDIO_VIEW_ITEMS } from '@/components/room/studio/studioViews';
import { STAGGER_MS } from '@roomtreatment/design';
import RollingNumber from '@/components/ui/RollingNumber';
import ChecklistMark from '@/components/room/gear/ChecklistMark';
import { PRODUCT_TILE_BG } from '@/components/room/gear/GearImageTile';
import type { ShowcaseProduct } from '@/lib/homeShowcase';
import { seatCurves } from './curves';
import { formatUsd } from './formatUsd';

const SPRING = 'var(--rt-spring-ms) var(--rt-ease-spring)';
const FADE = 'var(--rt-fade-ms) linear';
/** Delays are stagger steps, never picked milliseconds (motion.test.ts). */
const after = (steps: number) => steps * STAGGER_MS;

/** The editor's own lens switch, not a copy of its labels. */
const LENSES = STUDIO_VIEW_ITEMS;
/** Which lens each step happens in. */
const STEP_LENS = [0, 2, 2, 1, 3] as const;

// ── Geometry (viewBox 560 × 400) ─────────────────────────────────────────
// A 5.2 × 4.1 m room at 69 px/m, left of a column the parts dock into.
const ROOM = { x: 36, y: 44, w: 360, h: 284 };
const PLACED = { l: [136, 96], r: [296, 96], seat: [216, 235] } as const;
// The optimizer's answer: pulled off the front wall, the seat forward out of
// the back of the room. Still an equilateral triangle — a scaled copy of the
// placed one about (216, 96): x' = 0.8x + 43.2, y' = 0.8y + 31.2.
const OPT_SCALE = 0.8;
const OPT_SHIFT = [43.2, 31.2] as const;
const optimized = (p: readonly [number, number]) =>
  [p[0] * OPT_SCALE + OPT_SHIFT[0], p[1] * OPT_SCALE + OPT_SHIFT[1]] as const;

/** The result card's plot, flatter than the room so the card clears the seat. */
const PLOT = { w: 236, h: 40 } as const;
const { before: BEFORE, after: AFTER } = seatCurves(PLOT.w, PLOT.h);

function at(step: number, from: number): boolean {
  return step >= from;
}

/**
 * A layer fading in or out of the picture. Faded out, it also leaves the
 * render tree (`visibility`, flipped once the fade has run): a label at zero
 * opacity still has a box, and the page's collision check would find two
 * states of the story stacked on each other. Fading in, it is visible from
 * the moment its fade starts.
 */
function fade(on: boolean, delayMs = 0) {
  return {
    style: { opacity: on ? 1 : 0, visibility: on ? 'visible' : 'hidden' } as CSSProperties,
    transition: `opacity ${FADE} ${on ? delayMs : 0}ms, visibility 0s linear ${on ? `${delayMs}ms` : 'var(--rt-fade-ms)'}`,
  };
}

/** An element that appears from a step on, rising slightly as it does. */
function appear(step: number, from: number, delayMs = 0): CSSProperties {
  const on = at(step, from);
  const f = fade(on, delayMs);
  return {
    ...f.style,
    transform: on ? 'none' : 'translateY(-10px)',
    transition: `${f.transition}, transform ${SPRING} ${on ? delayMs : 0}ms`,
  };
}

/** A stroke that draws itself from a step on. `length` ≥ the path's length. */
function draw(step: number, from: number, length: number, delayMs = 0): CSSProperties {
  const on = at(step, from);
  return {
    strokeDasharray: length,
    strokeDashoffset: on ? 0 : length,
    transition: `stroke-dashoffset 900ms var(--rt-ease-spring) ${on ? delayMs : 0}ms`,
  };
}

function Marker({ x, y, label, step, from, delay, move }: {
  x: number; y: number; label: string; step: number; from: number; delay: number;
  move?: readonly [number, number];
}) {
  const on = at(step, from);
  const [tx, ty] = move && at(step, 3) ? [move[0] - x, move[1] - y] : [0, 0];
  const f = fade(on, delay);
  return (
    <g
      style={{
        ...f.style,
        transform: `translate(${tx}px, ${ty + (on ? 0 : -14)}px)`,
        transition: `${f.transition}, transform ${SPRING} ${on ? delay : 0}ms`,
      }}
    >
      <rect x={x - 11} y={y - 11} width={22} height={22} rx={3}
        fill="var(--rt-raised)" stroke="var(--rt-ink)" strokeWidth={2} />
      <text x={x} y={y + 4.5} textAnchor="middle" fontSize={12} fontWeight={700} fill="var(--rt-ink)">{label}</text>
    </g>
  );
}

export default function JourneyStage({ step, entered, system }: {
  step: number;
  /** The section has come into view: the parts arrive and the total counts up. */
  entered: boolean;
  /** The parts, from the catalogue (lib/homeShowcase.ts): the same system the
   *  hero shows, with the Gear rail's own renders. */
  system: readonly ShowcaseProduct[];
}) {
  const lens = STEP_LENS[Math.max(0, Math.min(STEP_LENS.length - 1, step))];
  const total = system.reduce((s, p) => s + p.priceUsd, 0);
  const seatAt = at(step, 3) ? optimized(PLACED.seat) : PLACED.seat;
  const docked = at(step, 1);

  return (
    <div
      data-testid="journey-stage"
      aria-hidden
      // Not `data-step`: the step list owns that name (Journey.tsx).
      data-stage-step={step}
      // `guided-theme`: a picture of the product, so it reads the product's
      // tokens — `.ed-label` and friends resolve to nothing outside it.
      className="journey-stage guided-theme @container relative overflow-hidden rounded-2xl border border-[var(--app-border)] bg-[var(--rt-paper)] ed-elev-2"
    >
      {/* The lens strip: which Studio view this step happens in. */}
      <div className="flex items-center justify-between border-b border-[var(--app-border)] px-4 py-3">
        <div className="relative grid grid-cols-4 rounded-full bg-[var(--rt-hover)] p-[3px]">
          <span
            className="absolute bottom-[3px] top-[3px] w-[calc((100%-6px)/4)] rounded-full border border-[var(--rt-hairline)] bg-[var(--rt-raised)] shadow-[0_1px_2px_color-mix(in_srgb,var(--rt-shadow)_16%,transparent)]"
            style={{
              left: 3,
              transform: `translateX(${lens * 100}%)`,
              transition: `transform ${SPRING}`,
            }}
          />
          {LENSES.map(({ icon: Icon, label }, i) => (
            <span
              key={label}
              className={`relative inline-flex h-7 items-center justify-center gap-1.5 px-3 text-body-sm font-medium transition-colors ${i === lens ? 'text-[var(--rt-ink)]' : 'text-[var(--rt-ink-dim)]'}`}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </span>
          ))}
        </div>
        <span className="hidden text-body-sm text-[var(--rt-ink-dim)] sm:inline">Living room</span>
      </div>

      <div className="relative" style={{ aspectRatio: '560 / 400' }}>
        <svg viewBox="0 0 560 400" className="absolute inset-0 h-full w-full">
          <defs>
            <pattern id="journey-grid" width="23" height="23" patternUnits="userSpaceOnUse">
              <path d="M 23 0 L 0 0 0 23" fill="none" stroke="var(--rt-hairline)" strokeWidth="1" />
            </pattern>
            <linearGradient id="journey-field" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--rt-accent)" stopOpacity="0.16" />
              <stop offset="0.3" stopColor="var(--rt-accent)" stopOpacity="0.05" />
              <stop offset="0.55" stopColor="var(--rt-accent)" stopOpacity="0.1" />
              <stop offset="0.8" stopColor="var(--rt-accent)" stopOpacity="0.04" />
              <stop offset="1" stopColor="var(--rt-accent)" stopOpacity="0.12" />
            </linearGradient>
          </defs>

          {/* 1 · the room */}
          <rect x={ROOM.x} y={ROOM.y} width={ROOM.w} height={ROOM.h} fill="url(#journey-grid)" style={appear(step, 1)} />
          <rect
            x={ROOM.x} y={ROOM.y} width={ROOM.w} height={ROOM.h}
            fill="url(#journey-field)"
            style={{ opacity: at(step, 3) ? 1 : 0, transition: `opacity 900ms linear ${at(step, 3) ? after(3) : 0}ms` }}
          />
          <rect
            x={ROOM.x} y={ROOM.y} width={ROOM.w} height={ROOM.h}
            fill="none" stroke="var(--rt-ink)" strokeWidth={5} strokeLinejoin="round"
            style={draw(step, 1, 1300)}
          />
          <g style={appear(step, 1, after(5))}>
            <line x1={ROOM.x} y1={ROOM.y - 18} x2={ROOM.x + ROOM.w} y2={ROOM.y - 18} stroke="var(--rt-ink-dim)" strokeWidth={1} />
            <rect x={ROOM.x + ROOM.w / 2 - 26} y={ROOM.y - 27} width={52} height={18} fill="var(--rt-paper)" />
            <text x={ROOM.x + ROOM.w / 2} y={ROOM.y - 14} textAnchor="middle" fontSize={11.5} fill="var(--rt-ink-dim)">5.20 m</text>
            <line x1={ROOM.x - 18} y1={ROOM.y} x2={ROOM.x - 18} y2={ROOM.y + ROOM.h} stroke="var(--rt-ink-dim)" strokeWidth={1} />
            <rect x={ROOM.x - 27} y={ROOM.y + ROOM.h / 2 - 26} width={18} height={52} fill="var(--rt-paper)" />
            <text x={ROOM.x - 18} y={ROOM.y + ROOM.h / 2 + 4} textAnchor="middle" fontSize={11.5} fill="var(--rt-ink-dim)"
              transform={`rotate(-90 ${ROOM.x - 18} ${ROOM.y + ROOM.h / 2})`}>4.10 m</text>
          </g>
          {/* The console and the sofa: what the room already holds. The sofa
              is where the seat is, so it goes where the optimizer moves it. */}
          <g style={appear(step, 1, after(7))}>
            <rect x={176} y={50} width={80} height={14} rx={3} fill="var(--rt-hover)" stroke="var(--rt-hairline-strong)" />
          </g>
          <g style={appear(step, 1, after(8))}>
            <g
              style={{
                transform: `translate(${seatAt[0] - PLACED.seat[0]}px, ${seatAt[1] - PLACED.seat[1]}px)`,
                transition: `transform ${SPRING} ${at(step, 3) ? after(3) : 0}ms`,
              }}
            >
              <rect x={PLACED.seat[0] - 42} y={PLACED.seat[1] - 6} width={84} height={30} rx={8}
                fill="var(--rt-hover)" stroke="var(--rt-hairline-strong)" />
            </g>
          </g>

          {/* 3 · treatment: panels at the side-wall reflection points, traps in the corners */}
          <g style={appear(step, 3, after(6))}>
            <rect x={ROOM.x + 3} y={124} width={7} height={46} rx={2} fill="var(--rt-ink)" />
            <rect x={ROOM.x + ROOM.w - 10} y={124} width={7} height={46} rx={2} fill="var(--rt-ink)" />
            <path d={`M ${ROOM.x + 2.5} ${ROOM.y + 2.5} l 26 0 l -26 26 z`} fill="var(--rt-ink)" opacity={0.85} />
            <path d={`M ${ROOM.x + ROOM.w - 2.5} ${ROOM.y + 2.5} l -26 0 l 26 26 z`} fill="var(--rt-ink)" opacity={0.85} />
          </g>

          {/* 2 · the listening triangle (moves with the system in step 3) */}
          <g
            style={{
              ...fade(at(step, 2), after(6)).style,
              transformOrigin: '0 0',
              transform: at(step, 3)
                ? `translate(${OPT_SHIFT[0]}px, ${OPT_SHIFT[1]}px) scale(${OPT_SCALE})`
                : 'none',
              transition: `${fade(at(step, 2), after(6)).transition}, transform ${SPRING}`,
            }}
          >
            <polyline
              points={`${PLACED.l[0]},${PLACED.l[1]} ${PLACED.seat[0]},${PLACED.seat[1]} ${PLACED.r[0]},${PLACED.r[1]}`}
              fill="none" stroke="var(--rt-accent)" strokeOpacity={0.7} strokeWidth={1.5} vectorEffect="non-scaling-stroke"
            />
            <line x1={PLACED.l[0]} y1={PLACED.l[1]} x2={PLACED.r[0]} y2={PLACED.r[1]}
              stroke="var(--rt-accent)" strokeOpacity={0.4} strokeWidth={1.2} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
          </g>

          {/* 2 · the system in the room */}
          <Marker x={PLACED.l[0]} y={PLACED.l[1]} label="L" step={step} from={2} delay={after(0)} move={optimized(PLACED.l)} />
          <Marker x={PLACED.r[0]} y={PLACED.r[1]} label="R" step={step} from={2} delay={after(1)} move={optimized(PLACED.r)} />
          <Marker x={ROOM.x + 26} y={ROOM.y + 262} label="S" step={step} from={2} delay={after(2)} move={[ROOM.x + 40, ROOM.y + 40]} />
          <g
            style={{
              ...fade(at(step, 2), after(3)).style,
              transform: `translate(${seatAt[0] - PLACED.seat[0]}px, ${seatAt[1] - PLACED.seat[1] + (at(step, 2) ? 0 : -14)}px)`,
              transition: `${fade(at(step, 2), after(3)).transition}, transform ${SPRING} ${at(step, 2) ? after(3) : 0}ms`,
            }}
          >
            <circle cx={PLACED.seat[0]} cy={PLACED.seat[1]} r={11} fill="var(--rt-seat)" />
            <g style={{ ...fade(at(step, 2) && !at(step, 4)).style, transition: fade(at(step, 2) && !at(step, 4)).transition }}>
              <rect x={PLACED.seat[0] - 19} y={PLACED.seat[1] - 42} width={38} height={18} rx={4}
                fill="var(--rt-raised)" stroke="var(--rt-hairline-strong)" />
              <text x={PLACED.seat[0]} y={PLACED.seat[1] - 29} textAnchor="middle" fontSize={11.5} fill="var(--rt-ink)">60°</text>
            </g>
          </g>
        </svg>

        {/* 0 · the parts: centred as the system is chosen, then docked to the
            side of the drawing for the rest of the story. */}
        {/* Classes rather than inline style for the geometry: a phone-width
            stage has room beside the drawing for the glyphs only, and only a
            container query knows the stage's width. */}
        <div
          className={
            'absolute flex flex-col gap-2 transition-[right,top,width,translate] duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] '
            + (docked
              ? 'right-[2.5%] top-[11%] w-[24%] @max-[30rem]:w-[13%]'
              : 'right-1/2 top-1/2 w-[62%] translate-x-1/2 -translate-y-1/2')
          }
        >
          {system.map((p, i) => (
            <PartCard
              key={p.id}
              icon={<Image src={p.image} alt="" width={64} height={64} className="h-[82%] w-[82%] object-contain" />}
              compact={docked}
              index={i}
              entered={entered}
            >
              <span
                className={`block truncate text-body-sm font-semibold text-[var(--rt-ink)] ${docked ? '@max-[30rem]:hidden' : ''}`}
              >
                {docked ? p.model : p.name}
              </span>
              {!docked && (
                <span className="block truncate text-caption text-[var(--rt-ink-dim)]">
                  {p.kind} · {formatUsd(p.priceUsd)}
                </span>
              )}
            </PartCard>
          ))}
          <div
            className="flex items-center justify-between gap-2 rounded-xl px-1 pt-1"
            style={{ ...fade(!docked).style, transition: fade(!docked).transition }}
          >
            <span className="inline-flex items-center gap-2 text-body-sm text-[var(--rt-ink)]">
              <ChecklistMark state="done" className="h-5 w-5" iconClassName="h-3 w-3" />
              Everything connects
            </span>
            <RollingNumber value={entered ? total : 0} prefix="$" className="text-title font-semibold text-[var(--rt-ink)]" />
          </div>
        </div>

        {/* 4 · the result at the seat */}
        <div
          className="absolute inset-x-[6%] bottom-[5%] rounded-xl border border-[var(--app-border)] bg-[var(--rt-raised)] px-4 py-3 ed-elev-2"
          style={appear(step, 4, after(2))}
        >
          <div className="flex items-center justify-between">
            <span className="ed-label">At your seat</span>
            <span className="flex items-center gap-3 text-caption text-[var(--rt-ink-dim)]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-px w-4 bg-[var(--rt-ink-faint)]" />Before
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-0.5 w-4 bg-[var(--rt-ink)]" />After
              </span>
            </span>
          </div>
          <svg viewBox={`0 0 ${PLOT.w} ${PLOT.h}`} className="mt-2 h-auto w-full">
            <line x1={0} y1={PLOT.h / 2} x2={PLOT.w} y2={PLOT.h / 2} stroke="var(--rt-hairline)" strokeDasharray="2 3" />
            <path d={BEFORE} fill="none" stroke="var(--rt-ink-faint)" strokeWidth={1.2} style={draw(step, 4, 700, after(4))} />
            <path d={AFTER} fill="none" stroke="var(--rt-ink)" strokeWidth={2} strokeLinecap="round" style={draw(step, 4, 400, after(10))} />
          </svg>
        </div>
      </div>
    </div>
  );
}

function PartCard({ icon, children, compact, index, entered }: {
  icon: ReactNode; children: ReactNode; compact: boolean; index: number; entered: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl border border-[var(--app-border)] bg-[var(--rt-raised)] ed-elev-1"
      style={{
        padding: compact ? '6px 8px' : '10px 12px',
        ...fade(entered, after(index + 1)).style,
        transform: entered ? 'none' : 'translateY(10px)',
        transition: `padding ${SPRING}, ${fade(entered, after(index + 1)).transition}, transform ${SPRING} ${after(index + 1)}ms`,
      }}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: PRODUCT_TILE_BG }}>
        {icon}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}
